import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const PlyxLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = usePlyxAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/plyx";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to Plyx");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-16" data-testid="plyx-login-page">
      <div className="w-full max-w-md">
        <h1 className="font-display text-4xl sm:text-5xl mb-2 tracking-tight">Welcome back</h1>
        <p className="text-white/60 mb-8">Log in to keep watching, posting, and earning credit.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Email</label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="plyx-login-email"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/60 mb-1.5">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="plyx-login-password"
              className="plyx-input w-full px-4 py-3 rounded-md bg-white/8 border border-white/15 focus:outline-none focus:ring-2 focus:ring-[#FF2D7A]/50"
            />
            <div className="mt-2 text-right">
              <Link
                to="/plyx/forgot"
                data-testid="plyx-login-forgot-link"
                className="text-xs text-white/60 hover:text-white"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="plyx-login-submit"
            className="w-full py-3 rounded-full font-semibold text-[#0A0A0A] disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : "Log in"}
          </button>
        </form>
        <div className="mt-6 text-center text-sm text-white/60">
          No Plyx account yet?{" "}
          <Link to="/plyx/register" data-testid="plyx-login-to-register" className="underline hover:text-white">Sign up free</Link>
        </div>
        <div className="mt-3 text-center text-[11px] text-white/40">
          <Link to="/plyx/admin/login" data-testid="plyx-login-admin-link" className="hover:text-white/70">
            AfriMarket admin? Sign in here →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PlyxLogin;
